import { type FC, useEffect, useMemo, useState } from "react";

import { type BaseSpec } from "@moment/api-collab/api-types";
import { idOf } from "@moment/api-collab/differential/Tuple/index";
import { useCellServices } from "@moment/design-system";
import { AddIcon, ViewListIcon } from "@moment/icons";

import { Button } from "../Button";
import { ConfirmDialogWrapper } from "../ConfirmDialog";
import { Dialog } from "../Dialog";
import { Label } from "../Label";
import { TextboxAuto } from "../TextboxAuto";
import { isDesktopAppMode } from "../env";
import { cn } from "../utils";
import ColumnsPopover from "./ColumnsPopover";
import RecordCard from "./RecordCard";
import { useBaseView } from "./useBaseView";

export const ViewInitializer: FC<{ show: boolean; viewId: string }> = ({ show, viewId }) => {
	const { base } = useCellServices();

	const [tables, setTables] = useState<[string, BaseSpec["tables"][number]][]>([]);
	useEffect(() => {
		if (!base) {
			return;
		}
		const unsubscribe = base.subscribeMetadata((baseSpec) =>
			setTables(Object.entries(baseSpec.tables))
		);
		return () => unsubscribe?.();
	}, [base]);

	const viewHandle = useBaseView(viewId);

	return (
		show && (
			<>
				<div className="absolute inset-0 bg-primary z-app-toggles-expanded opacity-80 flex items-center justify-center border rounded-lg" />
				<div
					className={cn(
						"absolute z-dialog flex flex-col gap-2 bg-primary-raised rounded-lg border shadow-lg text-sm p-4 w-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
						{
							"max-w-xs": !(base?.status instanceof Error),
							"max-w-sm": base?.status instanceof Error,
						}
					)}
				>
					{!viewId || viewId === "" ? (
						<>
							<div className="text-sm font-semibold">viewId parameter is missing</div>
							<div className="text-xs font-normal whitespace-pre-wrap overflow-auto max-h-32 px-4 pb-4 -mx-4 -mb-4">
								Add a viewId to this component to initiailze a view.
							</div>
						</>
					) : base?.status instanceof Error ? (
						<>
							<div className="text-sm font-semibold">Error loading base:</div>
							<div className="text-xs font-normal font-mono whitespace-pre-wrap overflow-auto max-h-32 px-4 pb-4 -mx-4 -mb-4">
								{base.status.message}
							</div>
						</>
					) : (
						<>
							<div className="text-md font-semibold">New Cards</div>
							<Button
								preset="primary"
								variant="outlined"
								size="sm"
								className="w-full"
								startIcon={<AddIcon />}
								onClick={viewHandle.createBlankView}
							>
								Start Blank
							</Button>
							<Label className="mt-2">Or select a table</Label>
							<div className="flex flex-col gap-0 -mx-4">
								{tables.length === 0 && (
									<div className="text-sm italic text-secondary px-4">
										No tables found
									</div>
								)}
								{tables.map(([name]) => (
									<Button
										key={name}
										className="mx-2"
										preset="quaternary"
										size="sm"
										startIcon={<ViewListIcon />}
										onClick={() => viewHandle.createViewFromTable(name)}
									>
										{name}
										<div className="flex-grow" />
									</Button>
								))}
							</div>
						</>
					)}
				</div>
			</>
		)
	);
};

function BaseViewContent({ viewId }: { viewId?: string }) {
	const { base, currentUser } = useCellServices();
	const isPublishedMode = !isDesktopAppMode();

	const viewHandle = useBaseView(viewId);
	const showOverlay =
		(!isPublishedMode && viewHandle.spec === undefined) || base?.status instanceof Error;

	const allowStructuralEdits = useMemo(
		() => !isPublishedMode && viewHandle.isStoreOwner,
		[isPublishedMode, viewHandle.isStoreOwner]
	);
	const allowDataEdits = useMemo(
		() => viewHandle.canEditRows(currentUser?.id ?? undefined),
		[currentUser, viewHandle]
	);

	return (
		<div className="relative">
			<ViewInitializer show={showOverlay} viewId={viewId} />

			<Dialog
				open={viewHandle.rowDialogOpen}
				onOpenChange={(open) => !open && viewHandle.closeRowDialog()}
			>
				<div className={cn("flex flex-col space-y-2", { "min-h-72 p-4": showOverlay })}>
					<div className="flex flex-row">
						<TextboxAuto
							className={cn(
								"text-lg font-bold bg-transparent",
								viewHandle.spec?.viewTitle ? "font-bold" : "font-semibold italic"
							)}
							disabled={isPublishedMode}
							placeholder="Untitled"
							defaultValue={viewHandle.spec?.viewTitle}
							onChange={(e) => {
								viewHandle.updateViewTitle(e.target.value);
							}}
							expandable={false}
						/>
						<div className="flex flex-row space-x-1 mr-2 ml-auto">
							{allowStructuralEdits && <ColumnsPopover viewHandle={viewHandle} />}
							{/* TODO: Support this. */}
							{/* <Button preset="quaternary" size="md" className="!px-1.5">
							Filter
						</Button>
						<Button preset="quaternary" size="md" className="!px-1.5">
							Sort
						</Button>
						<IconButton
							elevation="flat"
							size="md"
							preset="tertiary"
							borderRadius="default"
							icon={<SearchOutlined />}
							label="Search"
						/> */}
						</div>
						<Button
							preset="primary"
							size="md"
							elevation="flat"
							startIcon={<AddIcon />}
							onClick={viewHandle.appendRow}
							disabled={!allowDataEdits}
						>
							New
						</Button>
					</div>
					{viewHandle.data.length > 0 &&
						viewHandle.data.map((item, index) => {
							const rowId = idOf(item.original);
							return (
								<RecordCard.Trigger
									key={rowId}
									row={item}
									recordIndex={index}
									viewHandle={viewHandle}
								/>
							);
						})}
					<div>
						<Button
							preset="quaternary"
							size="md"
							startIcon={<AddIcon />}
							onClick={viewHandle.appendRow}
							disabled={!allowDataEdits}
						>
							New Card
						</Button>
					</div>
				</div>
				{viewHandle.table &&
					viewHandle.openItemIndex !== undefined &&
					viewHandle.openItem && (
						<RecordCard.Content
							table={viewHandle.table}
							allowDataEdits={allowDataEdits}
							viewHandle={viewHandle}
						/>
					)}
			</Dialog>
		</div>
	);
}

export function BaseView({ viewId }: { viewId?: string }) {
	return (
		<ConfirmDialogWrapper>
			<BaseViewContent viewId={viewId} />
		</ConfirmDialogWrapper>
	);
}
